<template>
  <UiComponentWrapper class="page">
    <!-- Section > Hero -->
    <UiComponentWrapper class="jumbotron">
      <UiComponentImage
        v-if="data.page.heroBackground"
        :key="`${heroBackgroundHeight}-${heroBackgroundWidth}`"
        :alt="data.page.heroBackground.alt"
        class="background"
        :expected-size="heroBackgroundWidth"
        :extra-params="{
          fit: 'crop',
          h: heroBackgroundHeight,
          w: heroBackgroundWidth,
        }"
        :height="heroBackgroundHeight"
        :orientation="
          heroBackgroundWidth > heroBackgroundHeight ? 'landscape' : 'portrait'
        "
        :quality="[100, 80, 80]"
        :source="data.page.heroBackground.url"
        :width="heroBackgroundWidth"
      />
      <UiComponentContainer>
        <OrganismHero
          alignment="left"
          :primary-button="{
            ...signupOrUpgradeCallToAction,
            label: 'Header section CTA',
          }"
          :secondary-button="{
            ...requestDemoCallToAction,
            label: 'Header section CTA',
          }"
        >
          <template #heading>
            {{ data.page.heroTitle }}
          </template>
          <template #paragraph>
            {{ data.page.heroText }}
          </template>
        </OrganismHero>
      </UiComponentContainer>
    </UiComponentWrapper>
    <!-- End -->

    <!-- Section > Highlighted Data -->
    <UiComponentContainer class="highlighted-data">
      <UiComponentWrapper class="inner">
        <UiComponentWrapper
          v-for="{
            id,
            hoverText,
            number,
            text1,
            text2,
          } in data.page.highlightedData"
          :key="id"
          class="item"
          :class="{ hoverable: !!hoverText }"
          :data-hover-text="hoverText"
        >
          <UiComponentParagraph>{{ roundToNearest(number) }}</UiComponentParagraph>
          <UiComponentParagraph>{{ text1 }}</UiComponentParagraph>
          <AtomAnchor
            v-if="text2"
            to="https://video-engagement.org/score-calculator"
          >
            {{ text2 }}<UiComponentIcon type="arrow-right" />
          </AtomAnchor>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Grid -->
    <UiComponentContainer class="main-grid">
      <UiComponentWrapper class="heading">
        <UiComponentHeading :level="2">
          {{ page.gridTitle }}
        </UiComponentHeading>
        <UiComponentSelect
          id="filter"
          has-icon
          icon-type="filter"
          :options="allFilters"
          @select:change="setFilter($event)"
        />
      </UiComponentWrapper>
      <UiComponentWrapper class="list">
        <MoleculeCustomersHubCard
          v-for="({ type, ...content }, index) in allElementsOfTheGrid"
          :key="`item-${index}`"
          :content="content"
          :of-type="type"
          :screen-width="screenWidth"
        />
      </UiComponentWrapper>
      <MoleculePagination
        :count="numberOfPages"
        :folder-name="folderName"
        :root="localePath('customers')"
        :value="currentPage"
      />
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Medals & Grid -->
    <UiComponentWrapper class="medals-and-grid">
      <UiComponentContainer>
        <UiComponentHeading :level="2">
          {{ data.componentMedalsGrid.title }}
        </UiComponentHeading>
        <UiComponentWrapper class="inner">
          <UiComponentWrapper class="content">
            <UiComponentWrapper
              v-if="data.componentMedalsGrid.customer"
              class="testimonial"
            >
              <UiComponentParagraph class="quote">
                «{{ data.componentMedalsGrid.customer.quote }}»
              </UiComponentParagraph>
              <UiComponentWrapper class="footer">
                <UiComponentWrapper class="author">
                  <UiComponentImage
                    v-if="data.componentMedalsGrid.customer.avatar"
                    :alt="data.componentMedalsGrid.customer.avatar.alt"
                    class="avatar"
                    :expected-size="32"
                    :height="data.componentMedalsGrid.customer.avatar.height"
                    :source="data.componentMedalsGrid.customer.avatar.url"
                    :width="data.componentMedalsGrid.customer.avatar.width"
                  />
                  <UiComponentWrapper class="metadata">
                    <UiComponentParagraph class="name">
                      {{
                        data.componentMedalsGrid.customer.author
                      }}
                    </UiComponentParagraph>
                    <UiComponentParagraph class="position">
                      {{
                        data.componentMedalsGrid.customer.position
                      }}
                    </UiComponentParagraph>
                  </UiComponentWrapper>
                </UiComponentWrapper>
                <UiComponentImage
                  v-if="data.componentMedalsGrid.customer.logo"
                  :alt="data.componentMedalsGrid.customer.logo.alt"
                  class="logo"
                  :expected-size="16"
                  :extra-params="{
                    con: 100,
                    gam: 100,
                    monochrome: '000000',
                    sat: -100,
                    trim: 'color',
                    vib: 100,
                  }"
                  :height="data.componentMedalsGrid.customer.logo.height"
                  orientation="portrait"
                  :source="data.componentMedalsGrid.customer.logo.url"
                  use-natural-width
                  :width="data.componentMedalsGrid.customer.logo.width"
                />
              </UiComponentWrapper>
            </UiComponentWrapper>
            <UiComponentWrapper class="medals">
              <UiComponentWrapper
                v-for="{ id, icon } in data.componentCertification.medals || []"
                :key="`medal-${id}`"
                class="medal"
              >
                <UiComponentImage
                  v-if="icon"
                  :alt="icon.alt"
                  :expected-size="g2MedalImageWidth"
                  :height="icon.height"
                  orientation="portrait"
                  :source="icon.url"
                  :width="icon.width"
                />
              </UiComponentWrapper>
            </UiComponentWrapper>
          </UiComponentWrapper>
          <UiComponentWrapper class="image">
            <UiComponentImage
              v-if="data.componentMedalsGrid.image"
              :alt="data.componentMedalsGrid.image.alt"
              :expected-size="g2GridImageWidth"
              :height="data.componentMedalsGrid.image.height"
              :source="data.componentMedalsGrid.image.url"
              :width="data.componentMedalsGrid.image.width"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentContainer>
    </UiComponentWrapper>
    <!-- End -->

    <!-- Section > Get a Demo -->
    <OrganismGetADemo
      :content="data.componentDemo"
      :global="data.componentGlobal"
    />
    <!-- End -->
  </UiComponentWrapper>
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
})

// i18n
defineI18nRoute({
  locales: ['en', 'es', 'fr'],
  paths: {
    en: '/customers/page/[id]',
    es: '/clientes/pagina/[id]',
    fr: '/clients/page/[id]',
  },
})

// Composables
const { page } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { params } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { push } = useRouter()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Async Data
const { data } = await useFetch(`/api/content/pages/customers/${locale.value}`, {
  key: `page-customers-pages-[id]-${params.id}-${locale.value}`,
  query: {
    preview: state.preview,
  },
})

// Meta
useSeoMeta({
  title: data.value.page.titleSeo,
  description: data.value.page.descriptionSeo,
  ogTitle: data.value.page.titleSeo,
  ogDescription: data.value.page.descriptionSeo,
  twitterTitle: data.value.page.titleSeo,
  twitterDescription: data.value.page.descriptionSeo,
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${publicRuntimeConfig.baseUrl}${localePath('customers')}`,
    },
  ],
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.page.trackingBreadcrumbTitle,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

// Refs
const heroBackgroundHeight = ref(0)
const heroBackgroundWidth = ref(0)
const heroObserver = ref(null)

// Computed Properties
const allClassicStories = computed(() => {
  return data.value.allCustomerStories
    .filter(({ isOfTypeExample }) => !isOfTypeExample)
    .map(card => ({ ...card, type: 'classic-story' }))
})

const allExampleStories = computed(() => {
  return data.value.allCustomerStories
    .filter(({ isOfTypeExample }) => isOfTypeExample)
    .map(card => ({ ...card, type: 'example-story' }))
})

const allElementsOfTheGrid = computed(() => {
  const startAt = 15 + 24 * (currentPage.value - 2)
  return [...allClassicStories.value, ...allExampleStories.value]
    .map(story => ({
      ...story,
      readMore: data.value.page.gridWordingForReadMore,
    }))
    .sort(
      (a, b) =>
        new Date(b.publishedAt || b.updatedAt)
          - new Date(a.publishedAt || a.updatedAt),
    )
    .slice(startAt, startAt + 24)
})

const currentPage = computed(() => {
  return Number.parseInt(params.id)
})

const allFilters = computed(() => {
  const filters = [
    {
      disabled: true,
      name: data.value.page.gridFilterBy,
    },
    {
      name: data.value.page.gridWordingForAllFilters,
      value: null,
    },
  ]

  const industries = data.value.allIndustries
    .filter(({ id }) =>
      [...allClassicStories.value, ...allExampleStories.value]
        .filter(story => story.industry)
        .map(({ industry }) => industry.id)
        .includes(id),
    )
    .map(({ id: value, name }) => ({
      name,
      value,
    }))

  if (industries.length > 0) {
    filters.push(
      {
        disabled: true,
        name: data.value.page.gridWordingForIndustriesFilter,
      },
      ...industries,
    )
  }

  const locations = data.value.allLocations
    .filter(({ id }) =>
      [...allClassicStories.value, ...allExampleStories.value]
        .filter(story => story.location)
        .map(({ location }) => location.id)
        .includes(id),
    )
    .map(({ id: value, name }) => ({
      name,
      value,
    }))

  if (locations.length > 0) {
    filters.push(
      {
        disabled: true,
        name: data.value.page.gridWordingForLocationsFilter,
      },
      ...locations,
    )
  }

  const sizes = data.value.allSizes
    .filter(({ id }) =>
      [...allClassicStories.value, ...allExampleStories.value]
        .filter(story => story.size)
        .map(({ size }) => size.id)
        .includes(id),
    )
    .map(({ id: value, size: name }) => ({
      name,
      value,
    }))

  if (locations.length > 0) {
    filters.push(
      {
        disabled: true,
        name: data.value.page.gridWordingForSizesFilter,
      },
      ...sizes,
    )
  }

  const useCases = data.value.allUseCases
    .filter(({ id }) =>
      [...allClassicStories.value, ...allExampleStories.value]
        .filter(story => story.useCases.length > 0)
        .map(({ useCases }) => useCases.map(useCase => useCase.id))
        .flat()
        .includes(id),
    )
    .map(({ id: value, shortName: name }) => ({
      name,
      value,
    }))

  if (useCases.length > 0) {
    filters.push(
      {
        disabled: true,
        name: data.value.page.gridWordingForUseCasesFilter,
      },
      ...useCases,
    )
  }

  return filters
})

const folderName = computed(() => {
  return locale.value === 'es' ? 'pagina' : 'page'
})

const g2GridImageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return 550
    default:
      return 550
  }
})

const g2MedalImageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return 92
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return 130
    default:
      return 100
  }
})

const numberOfPages = computed(() => {
  return Math.ceil((data.value.allCustomerStories.length - 15) / 24) + 1
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: data.value.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: data.value.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: data.value.componentGlobal.ctaTryForFreeShort,
      }
})

// Methods
function onObserveHero() {
  const { offsetHeight, offsetWidth }
    = document.querySelector('.page .jumbotron')
  heroBackgroundHeight.value = offsetHeight
  heroBackgroundWidth.value = offsetWidth
}

function roundToNearest(value) {
  const parsedValue = value.replace(',', '').replace('%', '')
  const number = Number(parsedValue)
  return parsedValue.length > 2
    ? Number(Math.round(number / 100) * 100)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : value
}

function setFilter(id) {
  push(
    localePath({
      name: 'customers-filter-id',
      params: {
        id,
      },
    }),
  )
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  heroObserver.value.unobserve(document.querySelector('.page .jumbotron'))
})

onMounted(() => {
  heroObserver.value = new ResizeObserver(onObserveHero)
  heroObserver.value.observe(document.querySelector('.page .jumbotron'))

  page (data.value.page.trackName || data.value.page.trackNameFallback)
  setAvailableLanguages([locale.value])
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle,
    },
  ])
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex-col items-center;

  .jumbotron {
    @apply relative flex-col items-center -mt-14 pt-24 pb-36 bg-[#F5F5F5] z-0;

    @screen md {
      @apply -mt-18 pt-40 pb-[152px];
    }

    .background {
      @apply hidden z-0;

      @screen md {
        @apply absolute inset-0 flex rounded-none transform translate-x-[142px];
      }

      @screen lg {
        @apply translate-x-0;
      }
    }

    .hero {
      @apply z-10;

      @screen md {
        @apply items-start;
      }

      @screen xl {
        @apply w-span7;
      }

      :deep(.h1),
      :deep(.description) {
        @screen md {
          @apply text-left;
        }
      }

      :deep(.button-list) {
        @screen md {
          @apply items-start w-auto;
        }
      }
    }
  }

  .highlighted-data {
    @apply -mt-22 z-10;

    @screen md {
      @apply -mt-12;
    }

    .inner {
      @apply flex-col p-8 bg-white shadow-light2 rounded-4xl;

      @screen md {
        @apply flex-row flex-wrap justify-between p-10;
      }

      @screen lg {
        @apply px-12 py-8;
      }

      .item {
        @apply relative flex-col mt-8;

        @screen md {
          width: calc((100% - 40px) / 2);
        }

        @screen lg {
          @apply mt-0;
          width: calc((100% - 120px) / 4);
        }

        &:nth-child(1) {
          @apply mt-0;
        }

        &:nth-child(2) {
          @screen md {
            @apply mt-0;
          }
        }

        &.hoverable {
          &:hover {
            &::before {
              @apply opacity-100 visible;
            }

            p {
              &:first-child {
                &::before {
                  @apply opacity-100 visible;
                }
              }
            }
          }

          &::before {
            @apply absolute left-1/2 -top-5 w-max px-6 py-[14px] bg-grey-blue-100 text-winter-green-900 text-origin leading-7 rounded shadow-light4 opacity-0 invisible content-[attr(data-hover-text)] transform -translate-x-1/2 -translate-y-full z-10  transition-all duration-300 ease-in-out;
            max-width: calc(100vw - 48px);

            @screen md {
              @apply max-w-[340px];
            }
          }

          p {
            &:first-child {
              @apply relative;

              &::before {
                @apply absolute left-1/2 -top-5 h-4 w-4 bg-grey-blue-100 rounded-sm opacity-0 invisible content-[""] transform -translate-x-1/2 -translate-y-1/2 rotate-45 z-20 transition-all duration-300 ease-in-out;
              }
            }
          }
        }

        p {
          @apply self-start w-auto font-medium text-blizzard-600 text-4xl leading-13;
        }

        p + p {
          @apply font-normal text-grey-blue-600 text-origin leading-7;
        }

        a {
          @apply flex items-center font-medium text-livestorm-blue-700 text-sm leading-none mt-2;

          &.hover {
            i {
              @apply translate-x-1;
            }
          }

          i {
            @apply ml-1 text-livestorm-blue-700 text-base leading-none transform translate-x-0 transition-transform duration-300 ease-in-out;
          }
        }
      }
    }
  }

  .main-grid {
    @apply flex flex-col mt-14;

    .heading {
      @apply flex-col;

      @screen md {
        @apply flex-row flex-wrap justify-between items-center;
      }

      .h2 {
        @apply font-medium text-2xl leading-8;

        @screen mdminus {
          @apply text-[28px] leading-[34px];
        }

        @screen mdplus {
          @apply text-3xl leading-10;
        }
      }

      .select {
        @apply w-[290px] mt-6;

        @screen md {
          @apply mt-0;
        }
      }
    }

    .list {
      @apply flex-col mt-10;

      @screen md {
        @apply grid grid-cols-2 gap-10;
      }

      @screen lg {
        @apply grid-cols-3;
      }

      :deep(.card) {
        @apply flex mt-10 rounded-3xl overflow-hidden;

        @screen md {
          @apply col-span-1 mt-0;
        }

        &:first-child {
          @apply mt-0;
        }

        &.large {
          @screen md {
            @apply col-span-2;
          }
        }

        &.overview {
          @screen md {
            @apply col-span-2;
          }

          @screen lg {
            @apply col-span-1;
          }
        }
      }
    }

    .pagination {
      @apply mt-14;
    }
  }

  .medals-and-grid {
    @apply justify-center mt-14 py-16 bg-winter-green-100;

    @screen md {
      @apply mt-26;
    }

    .container {
      @apply flex-col items-center;

      h2 {
        @apply text-4xl leading-14 text-center;

        @screen mdminus {
          @apply font-normal text-5xl leading-16;
        }

        @screen mdplus {
          @apply text-6xl leading-20;
        }
      }

      .inner {
        @apply flex-col mt-16;

        @screen xl {
          @apply flex-row justify-between items-center;
        }

        .content {
          @apply flex-col items-center;

          @screen xl {
            @apply w-span6;
          }

          .testimonial {
            @apply flex-col p-8 border-blizzard-200 rounded-[24px];
            border-width: 3px;

            .quote {
              @apply text-base text-grey-blue-600 leading-7;
            }

            .footer {
              @apply flex-col items-center mt-4;

              @screen md {
                @apply flex-row justify-between;
              }

              .author {
                @apply items-center;

                .avatar {
                  @apply flex-shrink-0 rounded-full mr-3;
                }

                .metadata {
                  @apply flex-col;

                  @screen md {
                    @apply flex-row;
                  }

                  .name,
                  .position {
                    @apply text-base text-grey-blue-600 leading-7;
                  }

                  .name {
                    @apply font-medium text-winter-green-900;

                    @screen md {
                      @apply mr-1;
                    }
                  }
                }
              }

              .logo {
                @apply self-start flex-shrink-0 mt-4 rounded-none;

                @screen md {
                  @apply self-center mt-0;
                }

                img {
                  filter: invert(11%) sepia(44%) saturate(570%)
                    hue-rotate(145deg) brightness(90%) contrast(94%);
                }
              }
            }
          }

          .medals {
            @apply flex-wrap mt-16;

            @screen md {
              @apply justify-between;
            }

            .medal {
              @apply justify-center w-1/2 mt-4;

              @screen md {
                @apply w-auto mt-0;
              }

              &:nth-child(1),
              &:nth-child(2) {
                @apply mt-0;
              }

              picture {
                @apply rounded-none;
              }
            }
          }
        }

        .image {
          @apply justify-center mt-16;

          @screen xl {
            @apply mt-0;
            width: 550px;
          }

          picture {
            @apply rounded-none;

            @screen md {
              @apply transform -translate-x-5;
            }

            @screen xl {
              @apply transform-none;
            }
          }
        }
      }
    }
  }
}
</style>
